import React from 'react';
import classNames from 'classnames';

import Data, { _F } from 'data';
import Dialog from 'ui/dialog';

import { getIconPath } from 'common';
import Store from 'store';
import EditingContext from 'store/editingContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import Tooltip from 'ui/tooltip';
import { WithCardTooltip } from 'tooltip';

import { searchRegex } from 'utils';

import { CardEffects } from 'components/CardView';

import 'components/editor/CardEditor.scss';
import BlockClearButton, { builderHasCards } from './common';

let cardOrderCache = null;
function sortCards(cards) {
  if (!cardOrderCache) {
    const dst = cardOrderCache = {};
    const sets = Data.cardSets
      .map(set => ({...set, cards: set.cards.filter(id => Data.cards[id])}))
      .sort((a, b) => {
        if (a.cards.length !== b.cards.length) return b.cards.length - a.cards.length;
        return a.name.localeCompare(b.name);
      });
    let index = 0;
    for (const set of sets) {
      for (const id of set.cards) {
        if (!dst[id]) dst[id] = ++index;
      }
    }
  }

  cards = cards.filter(card => card && Data.cards[card.id]);
  cards.sort((a, b) => {
    const aid = (cardOrderCache[a.id] || 9999), bid = (cardOrderCache[b.id] || 9999);
    if (aid !== bid) return aid - bid;
    return Data.cards[a.id].name.localeCompare(Data.cards[b.id].name);
  });
  while (cards.length < 6) cards.push(null);
  return cards;
}

function BuilderCardSlot({ index, selected, editing }) {
  const card = Store.useBuilder(builder => builder.cards?.[index]);

  const cardData = Data.cards[card?.id];

  const dispatch = Store.useDispatch();

  const onContextMenu = React.useCallback(e => {
    if (!editing) return;
    e.preventDefault();
    dispatch.setBuilder(builder => {
      builder = {...builder};
      builder.cards = builder.cards?.slice() || [];
      builder.cards[index] = null;
      builder.cards = sortCards(builder.cards);
      return builder;
    });
  }, [dispatch, index, editing]);

  const onClickAwake = React.useCallback(e => {
    if (!editing) return;
    const awake = Math.floor(e.nativeEvent.offsetX / 18) + 1;
    dispatch.setBuilder(builder => {
      builder = {...builder};
      builder.cards = builder.cards?.slice() || [];
      if (builder.cards[index]) {
        const item = builder.cards[index] = {...builder.cards[index]};
        item.awake = Math.max(0, Math.min(5, item.awake === awake ? awake - 1 : awake));
      }
      return builder;
    });
  }, [dispatch, index, editing]);

  const onWheel = React.useCallback(e => {
    if (!editing) return;
    e.preventDefault();
    e.stopPropagation();
    const dir = (e.deltaY > 0 ? -1 : 1);
    dispatch.setBuilder(builder => {
      builder = {...builder};
      builder.cards = builder.cards?.slice() || [];
      if (builder.cards[index]) {
        const item = builder.cards[index] = {...builder.cards[index]};
        item.awake = Math.max(0, Math.min(5, item.awake + dir));
      }
      return builder;
    });
  }, [dispatch, index, editing]);

  if (!cardData) return (
    <li className="lap-card">
      <div className="lap-card-name">&nbsp;</div>
      <div className="lap-card-art lap-empty"/>
    </li>
  );
  return (
    <li className={classNames(`lap-card lap-card-${Data.itemGrades[cardData.grade - 1].color}`, {"lap-selected": selected?.includes(card.id)})}>
      <div className="lap-card-name">{_F('sys.tooltip.card_name', cardData.name)}</div>
      <WithCardTooltip id={card.id} awake={card.awake}>
        <div className="lap-card-art" onContextMenu={editing ? onContextMenu : null} onWheel={editing ? onWheel : null}>
          <img src={getIconPath(cardData.image)} alt={cardData.name}/>
          <div className="lap-card-border"/>
          <div className="lap-card-level" onClick={editing ? onClickAwake : null}>
            {!!card.awake && <div className={`lap-card-awake lap-card-awake-${card.awake}`}/>}
          </div>
        </div>
      </WithCardTooltip>
    </li>
  );
}

function BuilderCardView({ editing, onClick }) {
  const builder = Store.useBuilder();
  const [selected, setSelected] = React.useState(null);
  return (
    <div className="lap-CardView" onClick={onClick}>
      <ul className="lap-CardList">
        {[0, 1, 2, 3, 4, 5].map(index => <BuilderCardSlot key={index} index={index} selected={selected} editing={editing}/>)}
      </ul>
      <CardEffects cards={builder.cards} onSelect={setSelected}/>
    </div>
  );
}

const BuilderCardSetTooltip = ({ set }) => <CardEffects cards={set.cards.map(id => ({id, awake: 5}))}/>;

function BuilderCardListCard({ id, disabled, active, onClick }) {
  const card = Data.cards[id];
  const onClicked = React.useCallback(() => onClick(id), [onClick, id]);

  if (!card) return null;
  return (
    <li className={classNames(`lap-card lap-card-${Data.itemGrades[card.grade - 1].color}`, {"lap-disabled": disabled})}>
      <WithCardTooltip id={id}>
        <div className="lap-card-art" onClick={onClicked}>
          <img src={getIconPath(card.image)} alt={card.name}/>
          <div className="lap-card-border"/>
          {!!active && <div className="lap-card-active">{_F('sys.card.card_state_in_deck')}</div>}
        </div>
      </WithCardTooltip>
    </li>
  );
}

function BuilderCardList() {
  const [search, setSearch] = React.useState('');
  const builder = Store.useBuilder();
  const dispatch = Store.useDispatch();

  const onClick = React.useCallback(id => dispatch.setBuilder(builder => {
    builder = {...builder};
    builder.cards = builder.cards?.slice() || [];
    const index = builder.cards.findIndex(card => card?.id === id);
    if (index >= 0) {
      builder.cards[index] = null;
    } else {
      const empty = [0, 1, 2, 3, 4, 5].find(index => !Data.cards[builder.cards[index]?.id]);
      if (empty != null) {
        builder.cards[empty] = {id, awake: 0};
      }
    }
    builder.cards = sortCards(builder.cards);
    return builder;
  }), [dispatch]);

  const cardSets = React.useMemo(() => Data.cardSets
    .map(set => ({...set, count: set.cards.filter(id => Data.cards[id]).length}))
    .sort((a, b) => {
      if (a.count !== b.count) return b.count - a.count;
      return a.name.localeCompare(b.name);
    })
  , []);

  const onSetSearch = React.useCallback(e => setSearch(e.target.value), []);
  const onClearSearch = React.useCallback(() => setSearch(''), []);
  const searchRe = React.useMemo(() => search.trim() && searchRegex(search.trim(), true), [search]);
  return <>
    <div className="lap-Search">
      <input placeholder="Search" value={search} onChange={onSetSearch}/>
      {!!search.trim() && <FontAwesomeIcon icon={faTimesCircle} className="lap-clear" onClick={onClearSearch}/>}
    </div>
    <ul className="lap-CardList">
      {cardSets.map((set, index) => {
        if (searchRe && !set.name.match(searchRe) && !set.cards.some(id => Data.cards[id]?.name.match(searchRe))) return null;
        return (
          <li key={index}>
            <Tooltip tooltip={<BuilderCardSetTooltip set={set}/>}>
              <h3>{set.name}</h3>
            </Tooltip>
            <ul className="lap-set-cards lap-CardView">
              {set.cards.map(id => <BuilderCardListCard id={id} key={id} disabled={searchRe && !set.name.match(searchRe) && !Data.cards[id]?.name.match(searchRe)}
                active={builder.cards.find(item => item?.id === id)} onClick={onClick}/>)}
            </ul>
          </li>
        );
      })}
    </ul>
  </>;
}

function BuilderCardEditor() {
  return (
    <div className="lap-CardEditor">
      <BuilderCardView editing/>
      <BuilderCardList/>
    </div>
  );
}

export default function BuilderCards() {
  const editing = EditingContext.useStateView("builderEditing");
  const [showDialog, setShowDialog] = React.useState(false);

  const builder = Store.useBuilder();
  const onClear = React.useCallback(builder => ({...builder, cards: []}), []);

  return <>
    <div className="lap-BuilderBlock lap-BuilderCards">
      <div className="lap-BuilderBlockHeader">
        Cards
        {!!(editing && builderHasCards(builder)) && <BlockClearButton onClear={onClear}/>}
      </div>
      <BuilderCardView onClick={() => editing && setShowDialog(true)}/>
    </div>
    {!!editing && showDialog && (
      <Dialog modal className="lap-CardEditorDialog" onClose={() => setShowDialog(false)} title={`Editing cards`}>
        <Dialog.Content>
          <BuilderCardEditor/>
        </Dialog.Content>
      </Dialog>
    )}
  </>;
}
